export interface LabelledPoint {
  label: string
  text: string
}

export interface NumberedStep {
  step: string
  title: string
  text: string
}

export interface ProofMetric {
  value: string
  label: string
}

export const PAPER_META = {
  eyebrow: 'Concept paper · Executive summary',
  title: 'More brands. More attention. More often.',
  standfirst:
    'Snakker turns approved brand content into short, interactive campaign experiences — called Snaks — that HCPs actually finish. This paper explains the idea, why it matters commercially, what it costs, and how it is run.',
  readingTime: '5 minute read',
}

/** The one-minute version, read first by people who will not read the rest. */
export const KEY_POINTS: LabelledPoint[] = [
  {
    label: 'The problem',
    text: 'HCPs do not lack content. They lack time. Attention — not information — is the scarce resource in every market.',
  },
  {
    label: 'The format',
    text: 'A Snak is a short story you participate in: two to seven minutes, built to be finished, and interactive so every response returns data.',
  },
  {
    label: 'The economics',
    text: 'One master build per brand is versioned into many campaigns and localized per market, so the cost per campaign falls as the portfolio grows.',
  },
  {
    label: 'The commercial case',
    text: 'Attention compounds across the lifecycle: faster at launch, more share of pocket in growth, and a longer, flatter tail for foundational brands.',
  },
  {
    label: 'The operating model',
    text: 'Snak agency of record: we do not only make campaigns, we run them — across brands, countries and time, on one governance view.',
  },
]

export const SNAK_STEPS: NumberedStep[] = [
  {
    step: '01',
    title: 'Story',
    text: 'An opening that earns the next ten seconds — a recognizable clinical moment, not a product claim.',
  },
  {
    step: '02',
    title: 'Choice',
    text: 'The HCP decides, predicts or reacts. Participation replaces passive reading.',
  },
  {
    step: '03',
    title: 'Insight',
    text: 'The answer is revealed with referenced evidence and, where useful, peer comparison.',
  },
  {
    step: '04',
    title: 'Action',
    text: 'A clear takeaway and next step: full content, prescribing information, or a rep conversation.',
  },
]

export const SNAK_PAYOFFS: string[] = [
  'Short enough to finish.',
  'Interesting enough to continue.',
  'Interactive enough to learn from.',
]

export const LIFECYCLE_PRIORITIES: LabelledPoint[] = [
  {
    label: 'Launch — accelerate time to market',
    text: 'Patent-protected time is finite. Share and time lost at launch cannot be recovered later, so early adoption is worth the most.',
  },
  {
    label: 'Growth — win share of pocket',
    text: 'Relevance, adoption and frequency decide how much of each customer’s available spend the brand captures during its best years.',
  },
  {
    label: 'Foundational — protect and extend value',
    text: 'Once exclusivity and investment decline, small, frequent attention slows erosion and prolongs commercial life.',
  },
]

export const FORMAT_LOGIC: LabelledPoint[] = [
  {
    label: 'New treatments create a reason to learn',
    text: 'At launch, curiosity and professional responsibility make room for longer formats and longer intervals between exposures.',
  },
  {
    label: 'Foundational brands need a reason to re-engage',
    text: 'As familiarity grows, that learning imperative fades. Communication has to get shorter, sharper and more frequent to stay relevant.',
  },
]

export const ECONOMICS_POINTS: LabelledPoint[] = [
  {
    label: 'One master build per brand, per year',
    text: 'The strategic idea, script, interaction design and compliance work happen once and become the reusable asset.',
  },
  {
    label: 'Every later campaign is a version',
    text: 'Each new campaign in the year is a version of that master — a new hook, a new question, the same approved spine.',
  },
  {
    label: 'Localization per market',
    text: 'Language, references and local requirements are applied per market without rebuilding the experience.',
  },
  {
    label: 'Monitoring and insight while live',
    text: 'Each campaign is measured while it runs, so the next version starts from evidence rather than assumption.',
  },
]

export const VALUE_LOGIC: LabelledPoint[] = [
  {
    label: 'Small improvements, large bases',
    text: 'On a mature revenue base, a fraction of a percent in adoption, frequency or retention is worth more than most campaign budgets.',
  },
  {
    label: 'Compounding, not one-off',
    text: 'Continued attention shows up as steeper growth, a higher peak and a stronger tail — effects deliberately excluded from any single campaign figure.',
  },
]

export const GOVERNANCE_POINTS: string[] = [
  'Everything in flight across brands and markets, on one board.',
  'Exactly what is waiting on the brand team, and what is with us.',
  'How far HCPs get before they stop, market by market.',
  'Which campaign the calendar should prioritise next.',
]

export const OPERATING_PROOF: ProofMetric[] = [
  { value: '5', label: 'Companies with managed campaigns' },
  { value: '22', label: 'Brands campaigned' },
  { value: '23', label: 'Market localizations' },
  { value: '5', label: 'Regions covered' },
]

export const NEXT_STEPS: NumberedStep[] = [
  {
    step: '01',
    title: 'Pick one brand and one market',
    text: 'Choose a brand with approved content and a clear commercial question to answer.',
  },
  {
    step: '02',
    title: 'Build the master Snak',
    text: 'One master build, one hook question, full compliance and reference handling.',
  },
  {
    step: '03',
    title: 'Run a campaign roadmap',
    text: 'Version the master into a short sequence of campaigns so the audience is met more than once.',
  },
  {
    step: '04',
    title: 'Review the evidence, then scale',
    text: 'Completion, answers and drop-off decide what to localize next and which brands follow.',
  },
]

export const FOOTNOTE =
  'All figures in this paper and in the accompanying calculators are illustrative and intended to show the shape of the model, not a quotation. Delivered work referenced is shown with client permission in the presentation.'
